import React, {useEffect, useMemo} from "react";
import {Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import AlertDialog from "../alert-dialog";
import ModulesPlaceholder from "../modules-placeholder";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {
  clearTemplate,
  clearView,
  fetchTemplate,
  fetchView,
  resetState,
  setPackage
} from "../../state/resource-handler/resourceHandlerActions";
import {resourceHandlerSelector} from "../../state/resource-handler/resourceHandlerSelectors";
import {addSpinnerMessage, markSpinnerMessage} from "../../state/spinner/spinnerActions";

const VIEW_TEMPLATE_SPINNER = "resource-handler/view-template-fetch";

const ResourceHandler = ({
  categoryPath,
  packageId,
  packageTitle,
  packageMetadata,
  packageAttachments,
  packageApproved,
  resource,
  viewId
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const nodeId = useSelector(nodeIdSelector);
  const {view, template} = useSelector(resourceHandlerSelector);

  const templateId = useMemo(() => resource?.templateId, [resource]);

  const session = useMemo(() => {
    const session = sessionStorage.getItem("resourceBackup");
    if (session) {
      sessionStorage.removeItem("resourceBackup");
      return JSON.parse(session);
    } else {
      return null;
    }
  }, []);

  useEffect(() => {
    if (packageId) {
      dispatch(setPackage(packageId));
    }
  }, [packageId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const handleErrorDialogHide = () => {
    if (view?.hasError) dispatch(clearView(nodeId, viewId));
    if (template?.hasError) dispatch(clearTemplate(nodeId, templateId));
  };

  const handleViewTemplateFetch = ({nodeId, viewId, templateId}) => {
    if (viewId || templateId) {
      dispatch(
        addSpinnerMessage(VIEW_TEMPLATE_SPINNER, t("components.resourceHandler.viewTemplateFetch.messages.start"))
      );
      if (viewId) dispatch(fetchView(nodeId, viewId));
      if (templateId) dispatch(fetchTemplate(nodeId, templateId));
    }
  };

  useEffect(() => {
    if (viewId && templateId) {
      if (view && template) {
        dispatch(markSpinnerMessage(VIEW_TEMPLATE_SPINNER));
      }
    } else if ((viewId && view) || (templateId && template)) {
      dispatch(markSpinnerMessage(VIEW_TEMPLATE_SPINNER));
    }
  }, [viewId, templateId, view, template, dispatch]);

  return (
    <Call cb={handleViewTemplateFetch} cbParam={{nodeId, viewId, templateId}} disabled={!viewId && !templateId}>
      {(!viewId || (viewId && view) || (templateId && template)) && (
        <ModulesPlaceholder
          id={`resource-handler-${resource.type}`}
          sx={{
            height: "100%",
            "& > div": {
              height: "100%"
            },
            overflow: "auto"
          }}
          categoryPath={categoryPath}
          packageId={packageId}
          packageTitle={packageTitle}
          packageApproved={packageApproved}
          resourceId={resource.id}
          resource={resource}
          metadata={packageMetadata}
          attachments={packageAttachments}
          viewId={viewId}
          view={view}
          templateId={templateId}
          template={templateId && template}
          session={session}
        />
      )}
      {(view?.hasError || template?.hasError) && (
        <AlertDialog
          className="resource-handler__view-template-error__dialog"
          open={view?.hasError || template?.hasError}
          onClose={handleErrorDialogHide}
        >
          <DialogContent>
            {view?.hasError ? t("components.resourceHandler.dialogs.unavailableView.content") : null}
            {template?.hasError ? t("components.resourceHandler.dialogs.unavailableTemplate.content") : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleErrorDialogHide}>{t("commons.confirm.confirm")}</Button>
          </DialogActions>
        </AlertDialog>
      )}
    </Call>
  );
};

export default ResourceHandler;
